import type { ReactNode } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme, type ThemeScope } from '@app/theme/theme';
import { cx } from '@app/components/ui';

/**
 * Centered auth column: the logo disc (spinning conic ring, glowing halo,
 * logo fading in), then the page content. Fixed theme toggle top-right.
 */
export function AuthScreen({
  children,
  width = 420,
  themeScope = 'admin',
}: {
  children: ReactNode;
  width?: number;
  themeScope?: ThemeScope;
}) {
  const { isDark, toggle } = useTheme(themeScope);
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-12 overflow-hidden bg-bg text-t1">
      <button
        type="button"
        onClick={toggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="fixed top-4 right-4 z-10 w-10 h-10 rounded-full border border-line2 bg-fill text-t3 hover:text-t1 hover:bg-fill2 flex items-center justify-center"
      >
        {isDark ? <Sun size={18} strokeWidth={1.8} aria-hidden="true" /> : <Moon size={18} strokeWidth={1.8} aria-hidden="true" />}
      </button>
      <div className="relative w-full flex flex-col items-center" style={{ maxWidth: width }}>
        <LogoDisc />
        {children}
      </div>
    </div>
  );
}

export function LogoDisc() {
  return (
    <div className="relative w-[88px] h-[88px] mb-7" style={{ animation: 'wpLogoIn .9s var(--ease) both' }}>
      <div
        aria-hidden="true"
        className="absolute rounded-full"
        style={{
          inset: -40,
          background: 'radial-gradient(circle, rgba(208,48,69,.32) 0%, rgba(208,48,69,.1) 40%, transparent 70%)',
          filter: 'blur(20px)',
          animation: 'wpGlow 6s ease-in-out infinite',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'conic-gradient(from 0deg, var(--acc), rgba(208,48,69,0) 35%, var(--acc) 60%, rgba(208,48,69,0) 90%, var(--acc))',
          animation: 'wpSpin 8s linear infinite',
        }}
      />
      <div
        className="absolute rounded-full flex items-center justify-center"
        style={{ inset: 1.5, background: 'radial-gradient(circle at 50% 30%, var(--disc-a) 0%, var(--disc-b) 100%)' }}
      >
        <img src="/favicon.svg" alt="Worship Planner" className="w-[46px] h-[46px] rounded-[12px] block" />
      </div>
    </div>
  );
}

/** Title block above an auth card. */
export function AuthTitle({ eyebrow, title, sub }: { eyebrow?: ReactNode; title: ReactNode; sub?: ReactNode }) {
  return (
    <div className="text-center mb-7" style={{ animation: 'wpRise .8s .1s var(--ease) both' }}>
      {eyebrow && <div className="text-[13px] font-semibold text-acc tracking-[.04em] mb-2">{eyebrow}</div>}
      <h1 className="text-[28px] font-semibold tracking-[-0.025em] leading-tight">{title}</h1>
      {sub && <div className="text-[14.5px] text-t3 mt-2 leading-[1.55] wp-pretty">{sub}</div>}
    </div>
  );
}

/**
 * Replays the invalid-submit shake without remounting (which would drop focus):
 * alternating between two identically-defined animations restarts it.
 */
export const shakeClass = (count?: number) => (!count ? '' : count % 2 ? 'wp-shake' : 'wp-shake-b');

/** The raised card that holds an auth form. */
export function AuthCard({
  children,
  className,
  shake,
  as: Tag = 'div',
  onSubmit,
  noValidate,
}: {
  children: ReactNode;
  className?: string;
  /** Bump this number to replay the invalid-submit shake. */
  shake?: number;
  as?: 'div' | 'form';
  onSubmit?: (e: React.FormEvent<HTMLElement>) => void;
  noValidate?: boolean;
}) {
  return (
    <div className="w-full" style={{ animation: 'wpRise .8s .2s var(--ease) both' }}>
      <Tag
        onSubmit={onSubmit}
        noValidate={noValidate}
        className={cx('w-full bg-card border border-line rounded-[22px] shadow-wp', shakeClass(shake), className)}
      >
        {children}
      </Tag>
    </div>
  );
}
